import type { CandidateTopperProfessionalProps } from '@tempmee/react-blocks';
import { IUser } from '~/models';
import { getProfessionalName, toYearsOfExperience } from './professional';
import { isBestInDentalWinner, toAttendance, toRating, toShiftCount } from './candidateBadges';

/** The topper's data props, with the page's own slots left to the caller. */
export type TProfessionalTopperSignals = Omit<
  CandidateTopperProfessionalProps,
  'topperStyle' | 'roleCode' | 'actions' | 'overflowMenu' | 'testID' | 'actionsTestID'
>;

/**
 * A professional's profile → the signals `CandidateTopper` reads.
 *
 * Used where the page heads with one professional and has no counter offer to
 * read them from: a terminal shift's `job.offer_final_reciever`, and the
 * candidate on Candidate Details. It is the sibling of `toCandidateCardData`,
 * which maps a *counter offer*. The shared per-signal helpers (`toShiftCount`,
 * `toAttendance`, `isBestInDentalWinner`, `toYearsOfExperience`) are reused
 * rather than reimplemented, so the topper grades this professional exactly as
 * the Confirm Candidates page grades an applicant.
 *
 * `attendance` is left `undefined` for an ungraded professional — level 0 is a
 * real state, and since `react-blocks@0.16.0` made the prop optional the badge
 * is simply omitted instead of being given the lowest earned tier.
 *
 * `missingSpecialty`, `partialShift` and `counterRate` are deliberately absent:
 * the first is a qualification warning the office can no longer act on, and the
 * other two describe a counter offer's terms. `companyExperience` is left to the
 * caller, because only the caller knows where the worked-with flags live — the
 * shift payload does not carry them, the candidate's counter offers do.
 */
export const toProfessionalTopperData = (
  professional: IUser | null | undefined,
): TProfessionalTopperSignals => ({
  name: getProfessionalName(professional) ?? '',
  avatarUrl: professional?.profile_image,
  shiftCount: toShiftCount(professional),
  years: toYearsOfExperience(professional),
  rating: toRating(professional?.average_rating),
  attendance: toAttendance(professional) ?? undefined,
  // The shift payload carries the professional's own favourite flag, unlike the
  // counter offer, where it sits on the offer.
  isFavorite: Boolean(professional?.isFavorite),
  isBestInDental: isBestInDentalWinner(professional),
});
